import { EventEmitter, Injectable, Type } from '@angular/core';
import { FormattingService } from '../services/formatting.service';
import { util } from '../util';
import _ from 'underscore';
import { FormlyFieldConfig, FormlyTemplateOptions, ɵreverseDeepMerge as reverseDeepMerge } from '@ngx-formly/core';
import { IObject, IValueObject } from '@type_definitions/datatypes';
import { TextEditor, EditorCode } from '../editor/editor-config.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { GaStateService, TranslationCategory } from '../services/ga-state.service';
import { EMAIL_REGEXP, URL_REGEXP } from './ga-validate.service';
import { TranslationService } from '../translation/translation.service';
import { Validators } from '@angular/forms';

export interface IValidationMessage {
  [messageProperties:string]:string | ((error:any, field:FormlyFieldConfig) => string | Observable<string>);
}

export interface IFormlyAddOnComponent {
  component:Type<any>;
  inputs:IObject;
  outputs?:IObject;
}

export type OnChangeHandler = (newValue?:any, oldValue?:any) => void;

export interface IFormValueInfo {
  // this should be function to auto-detect changes in value
  label?: () => string;
  content?: () => string;
  component?: Type<any>;
  contentType: 'html' | 'component';
  // component input bindings
  inputs?: IObject;
  // component output bindings
  outputs?: IObject;
}

export interface IFieldOption extends IValueObject {
  disabled?:boolean; // in aRadio, aSelect, aSelect2 this will disable the option
  labelClass?:string; // in aRadio this will set label class of the radio label
  name?:string;
  group?:any;
  [key:string]:any;
}

export interface IExtraTemplateOptions extends FormlyTemplateOptions {
  // formly parent tag class
  className?:string;
  // formly id
  id?:string;
  // css info class to be applied to info element
  infoClass?:string;
  // css label class to be applied to field label
  labelClass?:string;
  // css wrapper class to be applied for anArray() container
  fieldArrayWrapperClass?:string;
  fieldArrayControlClass?:string;
  fieldArrayButtonsClass?:string;
  // css wrapper class to be applied to <formly-form> in field aGroup()
  fieldGroupWrapperClass?:string;
  // css wrapper class to be applied to field top container
  wrapperClass?:string;
  // css class to be applied into element group wrapper
  groupClass?:string;
  // property of displaying option text (used in aSelect, aSelect2, aMultiSelect)
  labelProp?:string | Function;
  // show spinner to formly field
  loading?:boolean;
  // search on templateOptions.options instead request to server
  localSearch?:boolean;
  // render formly without label wrapper (labelClass will be ignored)
  noWrapper?:boolean;
  // single option, used in aRadioButton(..)
  option?:any;
  // list of options, used in aSelect, aSelect2, aSearch, aMultiSelect, aRadio
  options?:any[];
  // show empty option (<option>) in aSelect2
  showEmptyOption?:boolean;
  // subscription to listen on templateOptions.options change
  optionsObserver?:Observable<any[]>;
  // subscription to listen on templateOptions.url change
  urlObserver?: Observable<string>;
  // subscription to listen on label change
  labelObserver?: Observable<string>;
  // group prop to be used when aSelect(..) include group options
  groupProp?:Function | string;
  // specifies if the initial option is included in the list of options of aSelect and aSelect2, default: true
  addInitialOption?:boolean;
  // default: true, always translate label
  translate?:boolean;
  // default: false, translate text inside <option> text
  translateOptions?:boolean;
  // specify url for aSearch / aMultiSelect
  url?:any;
  // css value class to be applied to control field (input)
  valueClass?:string;
  // css value for 'aFileUpload' button class
  fileUploadButtonClass?:string;
  // property to be used in set option value
  valueProp?:string | Function;
  // hide error validation detail
  noError?:boolean;
  // show focus in input field
  inputFocus?:boolean;
  // show addon text, e.g. number input with currency at the end (allow translation key text)
  addOn?:string;
  // visibility state for `addOn`
  showAddOn?:boolean;
  // show addon with component
  addOnButton?:IFormlyAddOnComponent;
  // show copy to clipboard
  copyToClipboard?:boolean;
  // display the value in a single line
  copyToClipboardInlineValue?:boolean;
  // info text with (i) icon after label
  info?:string;
  // should show info text per default
  showInfo?:boolean;
  // will not display info
  noInfo?:boolean;
  // don't add offset in info element
  noInfoOffset?:boolean;
  // set busy to formly field input
  busy?:boolean;
  // minimum search length before triggering search (aSearch)
  minSearchLength?:number;
  // show (x) icon in aSearch
  allowClear?:boolean;
  // aSearch: true (default) - auto remove the value of the search field on blur (set the model only when an option is selected from the list) / false - allow custom text in the search input when on blur (set the model any time when a key is pressed down)
  autoClear?:boolean;
  // track by to be used in aSelect, aSelect2, aSearch, aMultiSelect
  trackBy?:string;
  // show inline aRadio inputs
  inline?:boolean;
  // don't track by array of options
  noTrack?:boolean;
  // regex validation
  patternFormat?:string;
  // search function to invoke after user type in aSearch field
  searchFunction?:ISearchFunction;
  // TinyMCE
  editor?:TextEditor;
  // TinyMCE config code
  editorCode?:EditorCode;
  // add extra class to label
  labelNgClass?:string[] | string | object;
  // screen option for translation config
  screen?:string;
  // translate key for label
  translateKeyLabel?:string;
  // translate key for info
  translateKeyInfo?:string;
  // translate key for placeholder
  translateKeyPlaceholder?:string;
  // translate key to be used for show translation
  translateKey?:string;
  // translate category
  category?:string;
  // translation can be edited (true/false)
  editable?:boolean;
  // init values of translation
  initValues?:any;
  // if field option has placeholder
  hasPlaceholder?:boolean;
  // should or not show placeholder
  hidePlaceholder?:boolean;
  // function triggers when there is user change
  onChange?:OnChangeHandler;
  // function triggers on click for aLink
  onClick?:Function;
  // similar to (blur) event
  onBlur?:Function;
  // similar to (enter) event
  onEnter?:Function;
  // similar to (mouseout) event
  onMouseOut?:Function;
  // function or object property key to display value / label
  format?:Function | string;
  // object to store extra data
  data?:any;
  // expression to disable an element
  disableExpression?:Function;
  // property to disable an option (default 'disabled') used in aSelect, aSelect2, aMultiSelect
  disabledProp?:Function | string;
  // initial value of field value
  initialValue?:any;
  // component class to be used in aCustom(..)
  componentName?:Type<any>;
  // component input bindings (don't use function if possible)
  inputs?: IObject;
  // component output bindings
  outputs?: IObject;
  // validation error message
  validationErrorMessage?:string;
  // compare function to be used in aSelect, aSelect2
  compareWith?(o1:any, o2:any):boolean;
  // hide filename, default: true (aFileUpload)
  hideFilename?:boolean;
  // trigger upload this will be undefined when to.autoUpload is true, accept parameter of uploadContext (aFileUpload)
  startUpload?: EventEmitter<IObject>;
  // onUploadDone event method invoked after file has been successfully uploaded (aFileUpload)
  onUploadDone?(result:any):void;
  // onUploadFailed event method invoked after file upload is failed (aFileUpload)
  onUploadFailed?(message: string): void;
  // upload form data
  uploadContext?:{ [key:string]:string };
  // auto upload after user select file (default: true)
  autoUpload?:boolean;
  // this is to start upload when autoUpload is false, or you want to do something special after upload finished
  uploading?:boolean;
  // show hint
  hint?:string;
  // for aLink class
  linkClass?: string;
  // css class for aTitle
  titleClass?: string;
  // for aLink href
  href?: string;
  // for aLink target
  target?: '_blank' | '_self' | string;
  // parsing html result for aSelect2 / aSearch
  parseHtml?:boolean;
  // aSelect2 config
  autoCompleteType?:string;
  // ngSelect config if select should be multiple
  multiple?: boolean;
  // add function for ngSelect multiple or multiselect field
  addFunction?: { label?: string, onClick: (searchTerm: string) => void };
  // get value remotely this usually for search field and valueProp is defined
  getValueRemotely?: (value: any) => Promise<any>;
  // icon to be used in formly field link
  icon?:string;
  // value info is to display info / popover below control field
  valueInfo?: IFormValueInfo;
  // this will show field array buttons (e.g. remove / add) default is true, if type is boolean it will be applicable to all, if function then model would be per index
  showFieldArrayButtons?:boolean | ExpressionFunction;
  // ignore first onChange
  ignoreFirstOnChange?:boolean;
  // show busy button for radio type
  showBusyWhenDisabled?:boolean;
  // minimum date
  minDate?:Date;
  // maximum date
  maxDate?:Date;
  // number of rows for textarea fields
  rows?:number;
}

export interface IExtraFieldConfigurationObject extends FormlyFieldConfig {
  templateOptions?:IExtraTemplateOptions;
  data?:{
    fields?:IExtraFieldConfigurationObject[]; // this will be used to store group fields .withFields([])
    [key:string]:any; // store any field data here, this can be accessed from field.data.{key}
  };
  fieldGroup?:IExtraFieldConfigurationObject[];
  fieldArray?:IExtraFieldConfigurationObject;
}

export interface IFormlyCollapsibleFields {
  collapsed?:IExtraFieldConfigurationObject[];
  expanded?:IExtraFieldConfigurationObject[];
}

interface ISearchFunction {
  request(filter:string):Promise<any>;

  /* used to cancel/abort the previous request
   * mandatory when the same request function is called from multiple fields on the same view. eg: send-mail.service.ts
   * applies only to: aMultiSelect() and aSearch()
   */
  cancel?:() => void;
}

export type ExpressionFunction<T = boolean> = (model:any, formState?:any, field?:IExtraFieldConfigurationObject) => T;

export const DISABLED_OPTION_PROP = 'disabled';

export enum FormlyType {
  ARRAY = 'array',
  CHECKBOX = 'checkbox',
  CUSTOM = 'custom',
  DATE_INPUT = 'dateInput',
  COMBINED_DATE = 'combinedDate',
  FILE_UPLOAD = 'fileUpload',
  GROUP = 'group',
  HIDDEN = 'hidden',
  INPUT = 'input',
  LINK = 'link',
  RADIO = 'radio',
  RADIO_BUTTON = 'radioButton',
  SEARCH = 'search',
  SELECT = 'select',
  SELECT2 = 'select2',
  MULTI_SELECT = 'multiSelect',
  NG_SELECT = 'ngSelect',
  TEXTAREA = 'textarea',
  TITLE = 'title',
  VALUE = 'value',
  TEMPLATE = 'template',
  NUMBER = 'number'
}

export enum FormlyInputType {
  EMAIL = 'email',
  NUMBER = 'number',
  URL = 'url',
}

@Injectable()
export class GaFormlyService {
  public static FIELD_HORIZONTAL_WRAPPER:string = 'form-field-horizontal';

  constructor(
    private formattingService:FormattingService,
    private translationService:TranslationService,
    private stateService:GaStateService
  ) {
    /**/
  }

  public aCheckbox(key?:string, label?:string):FieldBuilder {
    return FieldBuilder.aField(this.formattingService, this.translationService, this.stateService, FormlyType.CHECKBOX, label, key);
  }

  public aCombinedDate(key?:string, label?:string):FieldBuilder {
    return FieldBuilder.aField(this.formattingService, this.translationService, this.stateService, FormlyType.COMBINED_DATE, label, key);
  }

  public aCustom(label?:string):FieldBuilder {
    return FieldBuilder.aField(this.formattingService, this.translationService, this.stateService, FormlyType.CUSTOM, label);
  }

  public aDateInput(key?:string, label?:string):FieldBuilder {
    return FieldBuilder.aField(this.formattingService, this.translationService, this.stateService, FormlyType.DATE_INPUT, label, key);
  }

  public aField(type?:string, key?:string, label?:string):FieldBuilder {
    return FieldBuilder.aField(this.formattingService, this.translationService, this.stateService, type, label, key);
  }

  public aGroup(key?:string, label?:string):FieldBuilder {
    return FieldBuilder.aField(this.formattingService, this.translationService, this.stateService, FormlyType.GROUP, label, key);
  }

  public aHidden(key:string):FieldBuilder {
    return FieldBuilder.aField(this.formattingService, this.translationService, this.stateService, FormlyType.HIDDEN, undefined, key).withoutWrapper();
  }

  public anArray(key:string, label?:string):FieldBuilder {
    return FieldBuilder.aField(this.formattingService, this.translationService, this.stateService, FormlyType.ARRAY, label, key);
  }

  public anInput(key?:string, label?:string):FieldBuilder {
    return FieldBuilder.aField(this.formattingService, this.translationService, this.stateService, FormlyType.INPUT, label, key);
  }

  public aRadio(key?:string, label?:string):FieldBuilder {
    return FieldBuilder.aField(this.formattingService, this.translationService, this.stateService, FormlyType.RADIO, label, key);
  }

  public aRadioButton(key?:string, label?:string):FieldBuilder {
    return FieldBuilder.aField(this.formattingService, this.translationService, this.stateService, FormlyType.RADIO_BUTTON, label, key).withoutWrapper();
  }

  public aSearch(key?:string, label?:string):FieldBuilder {
    return FieldBuilder.aField(this.formattingService, this.translationService, this.stateService, FormlyType.SEARCH, label, key);
  }

  public aSelect(key?:string, label?:string):FieldBuilder {
    return FieldBuilder.aField(this.formattingService, this.translationService, this.stateService, FormlyType.SELECT, label, key);
  }

  /* Deprecated! Use aNgSelect instead */
  public aSelect2(key?:string, label?:string):FieldBuilder {
    return FieldBuilder.aField(this.formattingService, this.translationService, this.stateService, FormlyType.SELECT2, label, key);
  }

  public aMultiSelect(key?:string, label?:string):FieldBuilder {
    return FieldBuilder.aField(this.formattingService, this.translationService, this.stateService, FormlyType.MULTI_SELECT, label, key);
  }

  public aNgSelect(key?:string, label?:string):FieldBuilder {
    return FieldBuilder.aField(this.formattingService, this.translationService, this.stateService, FormlyType.NG_SELECT, label, key);
  }

  public aTemplate(html:string):FieldBuilder {
    return FieldBuilder.aField(this.formattingService, this.translationService, this.stateService, FormlyType.TEMPLATE).withTemplate(html);
  }

  public aTextarea(key?:string, label?:string):FieldBuilder {
    return FieldBuilder.aField(this.formattingService, this.translationService, this.stateService, FormlyType.TEXTAREA, label, key);
  }

  public aTitle(title:string):FieldBuilder {
    return FieldBuilder.aField(this.formattingService, this.translationService, this.stateService, FormlyType.TITLE).withTemplate(title).withoutWrapper();
  }

  public aValue(key?:string, label?:string):FieldBuilder {
    return FieldBuilder.aField(this.formattingService, this.translationService, this.stateService, FormlyType.VALUE, label, key);
  }

  public aLink(label?:string, key?:string):FieldBuilder {
    return FieldBuilder.aField(this.formattingService, this.translationService, this.stateService, FormlyType.LINK, label, key);
  }

  public aFileUpload(key?:string, label?:string):FieldBuilder {
    return FieldBuilder.aField(this.formattingService, this.translationService, this.stateService, FormlyType.FILE_UPLOAD, label, key);
  }

  /**
   * @ngdoc method
   * @name SharedModule:GaFormlyService#fillField
   * @methodOf SharedModule:GaFormlyService
   *
   * @description Utility method for use with select or search fields that need options to be fetched from the server. The field will be disabled and show a loading indicator while results are being fetched.
   *
   * @param {IExtraFieldConfigurationObject} field - The select/search field that needs options to be set
   * @param {Promise} result - This promise will resolve the options that need to be set to the field
   * @param {boolean} preselectSingleOption - Preselect the option when there is only a single option in the list
   *
   * @return {Promise} can be used for chaining
   */
  public async fillField(field:IExtraFieldConfigurationObject | IExtraFieldConfigurationObject[], result:Promise<any> | any[], preselectSingleOption?:boolean):Promise<any> {
    const fields:IExtraFieldConfigurationObject[] = _.isArray(field)? field : [field];
    _.forEach(fields, field => {
      this.setFieldStatus(field, true);
    });

    // result can be a non-promise
    if(!util.isPromise(result)){
      result = Promise.resolve(result);
    }

    return (result as Promise<any>)
      .then((options:any) => {
        _.forEach(fields, field => {
          field.templateOptions.options = _.clone(options);

          // preselect first option when only a single option
          if (preselectSingleOption && options?.length === 1) {
            field.formControl?.setValue(options[0]);
          }
        });
        return options;
      })
      .finally(() => {
        _.forEach(fields, field => {
          this.setFieldStatus(field, false);
        });
      });
  }

  // update the status of a field (both loading and disabled OR individually)
  public setFieldStatus(field:IExtraFieldConfigurationObject, status:boolean, onlyLoading?:boolean, onlyDisabled?:boolean){
    if(util.notNullOrUndefinedOrEmpty(status)){
      field.templateOptions.loading = status;
      field.templateOptions.disabled = status;
    } else {
      if(util.notNullOrUndefinedOrEmpty(onlyLoading)) {
        field.templateOptions.loading = onlyLoading;
      }
      if(util.notNullOrUndefinedOrEmpty(onlyDisabled)) {
        field.templateOptions.disabled = onlyDisabled;
      }
    }
  }

  // get the list of boolean options. when value is specified return only a single matching option
  public getBooleanOptions = (value?:boolean):IValueObject | IValueObject[] => {
    const trueOption:IValueObject = {
      id: true,
      value: 'GA.GENERAL.YES'
    };
    const falseOption:IValueObject = {
      id: false,
      value: 'GA.GENERAL.NO'
    };

    if (util.notNullOrUndefinedOrEmpty(value)) {
      return value? trueOption : falseOption;
    } else {
      return [
        trueOption,
        falseOption
      ];
    }
  };

  // remove empty objects from an array (useful to filter the formly fields before rendering)
  public removeEmptyFields = (fields:IExtraFieldConfigurationObject[]):IExtraFieldConfigurationObject[] => {
    return _.filter(fields, (field:IExtraFieldConfigurationObject) => !_.isEmpty(field));
  };
}

export class FieldBuilder {
  private changeHandler:OnChangeHandler;
  private clickHandler:Function;
  private enterHandler:Function;
  private mouseOutHandler:Function; // mouse out of element
  private blurHandler:Function;
  private data:any;
  private disabled:boolean;
  private disableExpression:ExpressionFunction;
  private extraOptions:IExtraFieldConfigurationObject;
  private format:Function | string;
  private hide:boolean;
  private hideExpression:ExpressionFunction;
  private id:string;
  private inputType:string;
  private key:string;
  private label:string;
  private label$:BehaviorSubject<string>;
  private labelClass:string;
  private labelProp:string | Function;
  private loading:boolean;
  private noWrapper:boolean;
  private option:any;
  private options:any[] | IFieldOption[];
  private pattern:string;
  private placeholder:string;
  private hasPlaceholder:boolean;
  private required:boolean;
  private template:string;
  private translate:boolean;
  private translateOptions:boolean;
  private translateKey:string;
  private editable:boolean;
  private screen:string;
  private type:string;
  private validationMessages:IValidationMessage = {};
  private valueClass:string;
  private infoClass:string;
  private valueProp:string | Function;
  private url:string;
  private url$:BehaviorSubject<string>;
  private noError:boolean;
  private addOn:string;
  private showAddOn:boolean;
  private addOnButton:IFormlyAddOnComponent;
  private copyToClipboard?:boolean;
  private copyToClipboardInlineValue?:boolean;
  private info:string;
  private valueInfo: IFormValueInfo;
  private showInfoText?:boolean;
  private noInfoOffset?:boolean;
  private maxLength?:number;
  private minLength?:number;
  private minDate?:Date;
  private maxDate?:Date;
  private inline?:boolean;
  private groupProp?:Function | string;
  private disabledProp?:Function | string;
  private options$:BehaviorSubject<any[]>;
  private wrapperClass:string = 'col-12';
  private fieldArrayWrapperClass:string;
  private groupClass:string;
  private componentName:Type<any>;
  private hint:string;
  private placeholderHidden:boolean;
  private multiple:boolean;
  private noInfo:boolean;
  private fieldGroups:IExtraFieldConfigurationObject[];
  private trackBy:string;

  constructor(
    private formattingService:FormattingService,
    private translationService:TranslationService,
    private stateService:GaStateService
  ) {
  }

  public static aField(formattingService:FormattingService, translationService:TranslationService, gaStateService:GaStateService, type?:string, label?:string, key?:string):FieldBuilder {
    return new FieldBuilder(formattingService, translationService, gaStateService)
      .withType(type)
      .withLabel(label)
      .withKey(key);
  }

  public isRequired():FieldBuilder {
    this.required = true;
    return this;
  }

  public isNotRequired():FieldBuilder {
    this.required = false;
    return this;
  }

  public isHide():FieldBuilder {
    this.hide = true;
    return this;
  }

  public isMultiple():FieldBuilder {
    this.multiple = true;
    return this;
  }

  public isDisabled():FieldBuilder {
    this.disabled = true;
    return this;
  }

  public isLoading():FieldBuilder {
    this.loading = true;
    return this;
  }

  public withLoading(loading:boolean):FieldBuilder {
    this.loading = loading;
    return this;
  }

  public withRequired(required:boolean):FieldBuilder {
    this.required = required;
    return this;
  }

  /**
   * @ngdoc method
   * @name SharedModule:FieldBuilder#withValidationMessage
   * @methodOf SharedModule:FieldBuilder
   *
   * @description When supplying a string, this is evaluated as code, so for a literal string you have to provide e.g. '\'bla\''
   *
   * @param {string} key - Type validation key (e.g.: 'required')
   * @param {string} message - Custom text for the validation message
   *
   * @return {FieldBuilder} this
   */
  public withValidationMessage(key:string, message:string | ((error:any, field:FormlyFieldConfig) => string | Observable<string>)):FieldBuilder {
    this.validationMessages[key] = this.translationService.instant(<string>message);
    return this;
  }

  /**
   * @ngdoc method
   * @name SharedModule:FieldBuilder#withInfo
   * @methodOf SharedModule:FieldBuilder
   *
   * @description Display an info icon (i) below the field that allows to show an info text when (i) icon is clicked. Can be configured with showInfo() and withoutInfoOffset()
   *
   * @param {string} infoText - The info text to be displayed.
   * @return {FieldBuilder} this
   */
  public withInfo(infoText?:string):FieldBuilder {
    this.info = infoText;
    return this;
  }

  public withoutInfo():FieldBuilder {
    this.noInfo = true;
    return this;
  }

  public withValueInfo(opts: IFormValueInfo):FieldBuilder {
    this.valueInfo = opts;
    return this;
  }

  public withHint(hint?:string):FieldBuilder {
    this.hint = hint;
    return this;
  }

  /**
   * @ngdoc method
   * @name SharedModule:FieldBuilder#showInfo
   * @methodOf SharedModule:FieldBuilder
   *
   * @description Mark the info text to be initially visible
   *
   * @return {FieldBuilder} this
   */
  public showInfo():FieldBuilder {
    this.showInfoText = true;
    return this;
  }

  /**
   * @ngdoc method
   * @name SharedModule:FieldBuilder#withoutInfoOffset
   * @methodOf SharedModule:FieldBuilder
   *
   * @description Initially the info text is only under field's value. This method allows to spread the info text on both columns (label and value).
   *
   * @return {FieldBuilder} this
   */
  public withoutInfoOffset():FieldBuilder {
    this.noInfoOffset = true;
    return this;
  }

  public withDisabled(disabled:boolean):FieldBuilder {
    this.disabled = disabled;
    return this;
  }

  public withFieldArrayWrapperClass(wrapperClass:string):FieldBuilder {
    this.fieldArrayWrapperClass = wrapperClass;
    return this;
  }

  public withDisableExpression(disableExpression:ExpressionFunction):FieldBuilder {
    this.disableExpression = disableExpression;
    return this;
  }

  public withWrapperClass(wrapperClass:string):FieldBuilder {
    this.wrapperClass = wrapperClass;
    return this;
  }

  public withGroupClass(groupClass:string):FieldBuilder {
    this.groupClass = groupClass;
    return this;
  }

  public withLabelClass(labelClass:string):FieldBuilder {
    this.labelClass = labelClass;
    return this;
  }

  public withValueClass(valueClass:string):FieldBuilder {
    this.valueClass = valueClass;
    return this;
  }

  public withInfoClass(infoClass:string):FieldBuilder {
    this.infoClass = infoClass;
    return this;
  }

  public withType(type:string):FieldBuilder {
    this.type = type;
    return this;
  }

  public withInputType(inputType:string):FieldBuilder {
    this.inputType = inputType;
    return this;
  }

  public withLabel(label:string):FieldBuilder {
    this.label = label;
    return this;
  }

  /**
   * @ngdoc method
   * @name SharedModule:FieldBuilder#withPlaceholder
   * @methodOf SharedModule:FieldBuilder
   *
   * @description Define a placeholder for the form field. When no parameter is set, the label text is being used as placeholder.
   *
   * @param {string} placeholder - Set a text to the field placeholder
   *
   * @return {FieldBuilder} this
   */
  public withPlaceholder(placeholder?:string):FieldBuilder {
    this.placeholder = placeholder;
    this.hasPlaceholder = true;
    return this;
  }

  public hidePlaceholder():FieldBuilder {
    this.placeholderHidden = true;
    return this;
  }

  public withKey(k:string):FieldBuilder {
    this.key = k;
    return this;
  }

  public withId(id:string):FieldBuilder {
    this.id = id;
    return this;
  }

  /**
   * @ngdoc method
   * @name SharedModule:FieldBuilder#withEditable
   * @methodOf SharedModule:FieldBuilder
   *
   * @description Allow to edit the label, placeholder and info text of the field
   *
   * @param translateKey {string} - When a translation key is specified this is going to be used as a base for the translation, otherwise the field's key is going to be used
   * @param screen {string} - Provides the i18n screen name from where to load the translation
   * @return {FieldBuilder} this
   */
  public withEditable(translateKey?:string, screen?:string):FieldBuilder {
    this.translateKey = translateKey;
    this.screen = screen;
    return this;
  }

  public withTranslationScreen(screen?:string):FieldBuilder {
    this.screen = screen;
    return this;
  }

  public withFields(fields:IExtraFieldConfigurationObject[]):FieldBuilder {
    this.data = this.data || {};
    this.data.fields = fields;
    return this;
  }

  public withArrayFields(fields:IExtraFieldConfigurationObject[]):FieldBuilder {
    this.fieldGroups = fields;
    return this;
  }

  public withFormat(format:Function | string):FieldBuilder {
    this.format = format;
    return this;
  }

  public withTemplate(t:string):FieldBuilder {
    this.template = t;
    return this;
  }

  public withExtraOptions(extraOptions:IExtraFieldConfigurationObject):FieldBuilder {
    this.extraOptions = extraOptions;
    return this;
  }

  public withOption(option:any):FieldBuilder {
    this.option = option;
    return this;
  }

  public withOptions(options:any[] | IFieldOption[], translated?:boolean):FieldBuilder {
    this.options = options;
    this.translateOptions = translated;
    return this;
  }

  public onChange(changeHandler:OnChangeHandler):FieldBuilder {
    this.changeHandler = changeHandler;
    return this;
  }

  public onClick(clickHandler:Function):FieldBuilder {
    this.clickHandler = clickHandler;
    return this;
  }

  public onEnter(enterHandler:Function):FieldBuilder {
    this.enterHandler = enterHandler;
    return this;
  }

  public onMouseOut(mouseOutHandler:Function):FieldBuilder {
    this.mouseOutHandler = mouseOutHandler;
    return this;
  }

  public onBlur(blurHandler:Function):FieldBuilder {
    this.blurHandler = blurHandler;
    return this;
  }

  public withHideExpression(expression:ExpressionFunction):FieldBuilder {
    this.hideExpression = expression;
    return this;
  }

  public withPattern(pattern:string | RegExp, message?:string):FieldBuilder {
    this.pattern = <string>pattern;
    if (message) {
      this.withValidationMessage('pattern', message);
    }
    return this;
  }

  public withLabelProp(labelProp:string | Function):FieldBuilder {
    this.labelProp = labelProp;
    return this;
  }

  public withValueProp(valueProp:string | Function):FieldBuilder {
    this.valueProp = valueProp;
    return this;
  }

  public withGroupProp(groupProp:Function | string):FieldBuilder {
    this.groupProp = groupProp;
    return this;
  }

  public withUrl(url:string):FieldBuilder {
    this.url = url;
    return this;
  }

  public withAddOn(addOn?:string):FieldBuilder {
    this.addOn = addOn;
    this.showAddOn = true;
    return this;
  }

  public withAddOnButton(addOnButtonComponent:IFormlyAddOnComponent):FieldBuilder {
    this.addOnButton = addOnButtonComponent;
    return this;
  }

  public withClipboard(inlineValue?:boolean):FieldBuilder { // it applies only for aValue fields
    this.copyToClipboard = true;
    this.copyToClipboardInlineValue = inlineValue;
    return this;
  }

  public withoutWrapper():FieldBuilder {
    this.noWrapper = true;
    return this;
  }

  public withoutError():FieldBuilder {
    this.noError = true;
    return this;
  }

  public withMinLength(minLength:number):FieldBuilder {
    this.minLength = minLength;
    return this;
  }

  public withMaxLength(maxLength:number):FieldBuilder {
    this.maxLength = maxLength;
    return this;
  }

  public withMinDate(date:Date):FieldBuilder {
    this.minDate = date;
    return this;
  }

  public withMaxDate(date:Date):FieldBuilder {
    this.maxDate = date;
    return this;
  }

  public withoutEditable():FieldBuilder {
    this.editable = false;
    return this;
  }

  public withInline():FieldBuilder {
    this.inline = true;
    return this;
  }

  public withComponent(component:Type<any>):FieldBuilder {
    this.componentName = component;
    return this;
  }

  public withTrackBy(trackBy:string):FieldBuilder {
    this.trackBy = trackBy;
    return this;
  }

  public build():IExtraFieldConfigurationObject {
    this.extraOptions = this.extraOptions || {};
    this.extraOptions.className = this.key? this.formattingService.join(!this.noWrapper? 'col' : '', this.wrapperClass, `key-${this.formattingService.snakeCase(this.key)}`, ' ') : this.wrapperClass;
    this.extraOptions.data = this.data;
    this.extraOptions.hide = this.hide;
    this.extraOptions.hideExpression = this.hideExpression;
    this.extraOptions.id = this.id;

    this.extraOptions.templateOptions = this.extraOptions.templateOptions || {};
    this.extraOptions.templateOptions.disabled = this.disabled;
    this.extraOptions.templateOptions.disableExpression = this.disableExpression;
    this.extraOptions.templateOptions.labelProp = this.labelProp;
    this.extraOptions.templateOptions.loading = this.loading;
    this.extraOptions.templateOptions.noWrapper = this.noWrapper;
    this.extraOptions.templateOptions.onChange = this.changeHandler;
    this.extraOptions.templateOptions.onClick = this.clickHandler;
    this.extraOptions.templateOptions.onEnter = this.enterHandler;
    this.extraOptions.templateOptions.onMouseOut = this.mouseOutHandler;
    this.extraOptions.templateOptions.onBlur = this.blurHandler;
    this.extraOptions.templateOptions.option = this.option;

    if (this.trackBy) {
      this.extraOptions.templateOptions.trackBy = this.trackBy;
    }

    // fileupload
    if (this.type === FormlyType.FILE_UPLOAD) {
      const autoUpload: boolean = util.notNullOrUndefinedOrEmpty(this.extraOptions.templateOptions.autoUpload)? this.extraOptions.templateOptions.autoUpload : true;
      this.extraOptions.templateOptions.autoUpload = autoUpload;
      this.extraOptions.templateOptions.hideFilename = util.notNullOrUndefinedOrEmpty(this.extraOptions.templateOptions.hideFilename)? this.extraOptions.templateOptions.hideFilename : true;

      if (!autoUpload) {
        this.extraOptions.templateOptions.startUpload = new EventEmitter<IObject>();
      }
    }
    // used in ng-select
    this.extraOptions.templateOptions.multiple = this.extraOptions.templateOptions.multiple || this.multiple;

    // options handler
    this.extraOptions.templateOptions.options = this.options;
    this.options$ = new BehaviorSubject(this.options || []);
    this.extraOptions.templateOptions.optionsObserver = this.options$.asObservable();
    this.extraOptions.templateOptions.groupProp = this.groupProp;
    this.extraOptions.templateOptions.disabledProp = this.disabledProp;

    this.extraOptions.templateOptions.pattern = this.pattern;
    this.extraOptions.templateOptions.placeholder = this.placeholder;
    this.extraOptions.templateOptions.hasPlaceholder = this.hasPlaceholder;
    this.extraOptions.templateOptions.hidePlaceholder = this.placeholderHidden;
    this.extraOptions.templateOptions.translate = this.translate;
    this.extraOptions.templateOptions.translateOptions = this.translateOptions;
    this.extraOptions.templateOptions.type = this.inputType;
    this.extraOptions.templateOptions.valueProp = this.valueProp;

    this.extraOptions.templateOptions.url = this.url;
    if (this.type === FormlyType.LINK) {
      this.url$ = new BehaviorSubject(this.url);
      this.extraOptions.templateOptions.urlObserver = this.url$.asObservable();
    }

    this.extraOptions.templateOptions.noError = this.noError;
    this.extraOptions.templateOptions.addOn = this.addOn? this.translationService.instant(this.addOn) : undefined;
    this.extraOptions.templateOptions.showAddOn = this.extraOptions.templateOptions.showAddOn || this.showAddOn;
    this.extraOptions.templateOptions.addOnButton = this.addOnButton;
    this.extraOptions.templateOptions.copyToClipboard = this.copyToClipboard;
    this.extraOptions.templateOptions.copyToClipboardInlineValue = this.copyToClipboardInlineValue;
    this.extraOptions.templateOptions.minLength = this.minLength;
    this.extraOptions.templateOptions.maxLength = this.maxLength;
    this.extraOptions.templateOptions.minDate = this.minDate;
    this.extraOptions.templateOptions.maxDate = this.maxDate;
    this.extraOptions.templateOptions.inline = this.inline;
    this.extraOptions.templateOptions.groupClass = this.groupClass;
    this.extraOptions.templateOptions.fieldArrayWrapperClass = this.fieldArrayWrapperClass;
    this.extraOptions.templateOptions.componentName = this.componentName;

    if (util.notNullOrUndefinedOrEmpty(this.hint)) {
      this.extraOptions.templateOptions.hint = this.hint;
    }

    if (util.notNullOrUndefinedOrEmpty(this.info)) {
      this.extraOptions.templateOptions.info = this.info;
    }
    if (util.notNullOrUndefinedOrEmpty(this.showInfoText)) {
      this.extraOptions.templateOptions.showInfo = this.showInfoText;
    }
    this.extraOptions.templateOptions.noInfoOffset = this.noInfoOffset;
    this.extraOptions.templateOptions.noInfo = this.noInfo;
    this.extraOptions.templateOptions.infoClass = this.infoClass;

    if (util.notNullOrUndefinedOrEmpty(this.valueInfo)) {
      this.extraOptions.templateOptions.valueInfo = this.valueInfo;
    }

    this.extraOptions.validation = this.extraOptions.validation || {};
    this.extraOptions.validation.messages = this.validationMessages;

    // for checkbox fields validation of type 'required' means that the checkbox to be checked (value = true)
    if(this.required && this.type === FormlyType.CHECKBOX) {
      this.extraOptions.validators = this.extraOptions.validators || {};
      this.extraOptions.validators.validation = [Validators.requiredTrue];
    }

    // for formly array type
    if (this.type === FormlyType.ARRAY && util.notNullOrUndefinedOrEmpty(this.fieldGroups)) {
      this.extraOptions.fieldArray = this.extraOptions.fieldArray || {};
      this.extraOptions.fieldArray.fieldGroup = this.fieldGroups;
    }

    // these fields are used for translatable/editable fields
    this.extraOptions.templateOptions.translateKey = this.translateKey || _.clone(this.key);
    this.extraOptions.templateOptions.screen = this.screen || this.stateService.screen;
    this.extraOptions.templateOptions.category = this.getTranslationCategory();
    this.extraOptions.templateOptions.editable = this.isEditableField();

    _.each([
      'info',
      'label',
      'placeholder'
    ], (option:string) => {
      this.extraOptions.templateOptions[`translateKey${util.capitalize(option)}`] = `${this.extraOptions.templateOptions.screen}.${this.extraOptions.templateOptions.translateKey}.${option}`.toUpperCase();
    });

    // this is used for editable-text directive to set the initial values
    this.extraOptions.templateOptions.initValues = {
      label: this.label? this.translationService.instant(this.label) : this.label,
      info: this.info? this.translationService.instant(this.info) : this.info,
      placeholder: this.hasPlaceholder? (this.translationService.instant(this.placeholder? this.placeholder : this.label)) : undefined
    };

    if (this.type === FormlyType.TEMPLATE) {
      return this.createWithTemplate(this.template, this.label, this.key, this.required, this.extraOptions, this.labelClass, this.valueClass);
    } else if (this.type === FormlyType.TITLE) {
      const titleField:IExtraFieldConfigurationObject = this.createWithTemplate(this.template, this.label, this.key, this.required, this.extraOptions, this.labelClass, this.valueClass);
      const titleClass:string = util.notNullOrUndefined(this.extraOptions.templateOptions.titleClass) ? this.extraOptions.templateOptions.titleClass : 'form-title'; // defaults to form-title
      titleField.template = `<h4 class="${titleClass}">${this.translationService.instant(titleField.template)}</h4>`;
      return titleField;
    } else {
      return this.createWithType(this.type, this.label, this.key, this.required, this.format, this.labelClass, this.valueClass, this.extraOptions);
    }
  }

  private createWithType(type:string, label:string, key:string, required?:boolean,
    format?:Function | string, labelClass?:string, valueClass?:string,
    extraOptions?:IExtraFieldConfigurationObject):IExtraFieldConfigurationObject {

    const ff: IExtraFieldConfigurationObject = {
      type: type,
      key: key,
      templateOptions: {
        label: label,
        required: required,
        format: format,
        labelClass: labelClass,
        valueClass: valueClass
      }
    };
    this.afterCreate(ff, extraOptions);
    return ff;
  }

  private createWithTemplate(template:string, label?:string, key?:string, required?:boolean, extraOptions?:IExtraFieldConfigurationObject, labelClass?:string, valueClass?:string):IExtraFieldConfigurationObject {
    let preparedTemplate: string = template;
    // when wrapper class defined in 'templateOptions' for aTemplate() then wrap the element with 'wrapperClass' setting
    if (extraOptions.templateOptions.wrapperClass) {
      preparedTemplate = `<div class="${extraOptions.templateOptions.wrapperClass}">${template}</div>`;
    }

    const ff: IExtraFieldConfigurationObject = {
      template: preparedTemplate,
      key: key,
      templateOptions: {
        label: label,
        required: required,
        labelClass: labelClass,
        valueClass: valueClass
      },
      data: {
        custom: true
      }
    };
    this.afterCreate(ff, extraOptions);
    return ff;
  }

  private afterCreate(ff:IExtraFieldConfigurationObject, extraOptions:IExtraFieldConfigurationObject):void {
    // should be passed by reference, not copied to avoid scope problems.
    if (extraOptions && extraOptions.data && extraOptions.data.fields) {
      ff.data = {};
      ff.data.fields = extraOptions.data.fields;
    }

    reverseDeepMerge(ff, extraOptions);

    //merge some empty objects to make code simpler
    reverseDeepMerge(ff, {
      data: {},
      templateOptions: {},
      expressionProperties: {},
      validation: {
        messages: {}
      }
    });

    // getter setter of options. To make sure options observer also being triggered at the same time ( $scope.$watch alternative )
    Object.defineProperty(ff.templateOptions, 'options', {
      get: () => this.options,
      set: (value) => {
        this.options = value;
        this.options$.next(value);
      }
    });

    // this creates an Observer which allows to watch when url parameter changes
    if (this.type === FormlyType.LINK) {
      Object.defineProperty(ff.templateOptions, 'url', {
        get: () => this.url,
        set: (value) => {
          this.url = value;
          this.url$.next(value);
        }
      });
    }

    // this creates an Observer which allows to watch when label param changes
    if (this.label) {
      this.label$ = new BehaviorSubject(this.label);
      ff.templateOptions.labelObserver = this.label$.asObservable();

      Object.defineProperty(ff.templateOptions, 'label', {
        get: () => this.label,
        set: (value) => {
          if (value) {
            this.label = value;
            this.label$.next(value);
          }
        }
      });
    }

    if (util.notNullOrUndefinedOrEmpty(ff.templateOptions.disabled)) {
      //make sure is it added as ng-disabled by ngModelAttributesManipulator
      ff.expressionProperties['templateOptions.disabled'] = (_model:any, _formState:any, field:IExtraFieldConfigurationObject) => {
        return field.templateOptions.disabled;
      };
    }

    if (util.notNullOrUndefinedOrEmpty(ff.templateOptions.required)) {
      //make sure is it added as ng-required by ngModelAttributesManipulator
      ff.expressionProperties['templateOptions.required'] = (_model:any, _formState:any, field:IExtraFieldConfigurationObject) => {
        return field.templateOptions.required;
      };
    }

    if (util.notNullOrUndefinedOrEmpty(ff.templateOptions.pattern)) {
      //make sure is it added as ng-pattern by ngModelAttributesManipulator
      ff.expressionProperties['templateOptions.pattern'] = (_model:any, _formState:any, field:IExtraFieldConfigurationObject) => {
        return field.templateOptions.pattern;
      };
    }
    this.addDefaultInputType(ff);
    this.addInputType(ff);
    this.addValidatorMessages(ff);
  }

  private addValidatorMessages(ff:IExtraFieldConfigurationObject) {
    if (!this.readOnly(ff) && !ff.validation.messages.required) {
      ff.validation.messages['required'] = (_err:any, field:IExtraFieldConfigurationObject):string => {
        return this.translationService.instant('GA.VALIDATION.REQUIRED', { label: field.templateOptions.label });
      };
    }
    if (!this.readOnly(ff) && util.notNullOrUndefinedOrEmpty(ff.templateOptions.pattern)) {
      ff.validation.messages['pattern'] = ff.validation.messages['pattern'] || ((_err:any, field:IExtraFieldConfigurationObject):string => {
        if (!field.templateOptions.patternFormat) {
          return this.translationService.instant('GA.VALIDATION.INVALID', { label: field.templateOptions.label });
        } else {
          return this.translationService.instant('GA.VALIDATION.INVALID_FORMAT', {
            label: field.templateOptions.label,
            format: field.templateOptions.patternFormat
          });
        }
      });
    }
    if (!this.readOnly(ff) && util.notNullOrUndefinedOrEmpty(ff.templateOptions.required) && util.notNullOrUndefinedOrEmpty(ff.templateOptions.pattern)) {
      ff.validation.messages['required'] = (_err:any, field:IExtraFieldConfigurationObject):string => {
        if (!field.templateOptions.patternFormat) {
          return this.translationService.instant('GA.VALIDATION.REQUIRED', { label: field.templateOptions.label });
        } else {
          return this.translationService.instant('GA.VALIDATION.REQUIRED_FORMAT', {
            label: field.templateOptions.label,
            format: field.templateOptions.patternFormat
          });
        }
      };
    }
    if (!this.readOnly(ff) && util.notNullOrUndefinedOrEmpty(ff.templateOptions.minLength)) {
      ff.validation.messages['minlength'] = (_err:any, field:IExtraFieldConfigurationObject):string => {
        return this.translationService.instant('GA.VALIDATION.MINLENGTH', {
          label: field.templateOptions.label,
          minlength: field.templateOptions.minLength
        });
      };
    }
    if (!this.readOnly(ff) && util.notNullOrUndefinedOrEmpty(ff.templateOptions.maxLength)) {
      ff.validation.messages['maxlength'] = (_err:any, field:IExtraFieldConfigurationObject):string => {
        return this.translationService.instant('GA.VALIDATION.MAXLENGTH', {
          label: field.templateOptions.label,
          maxlength: field.templateOptions.maxLength
        });
      };
    }
    if (ff.templateOptions.type === FormlyInputType.EMAIL) {
      ff.templateOptions.pattern = EMAIL_REGEXP;
      ff.validation.messages.pattern = this.translationService.instant('GA.VALIDATION.EMAIL_VALIDATION');
    } else if (ff.templateOptions.type === FormlyInputType.URL) {
      ff.templateOptions.pattern = URL_REGEXP;
      ff.validation.messages.pattern = this.translationService.instant('GA.VALIDATION.URL');
    }
    if (ff.templateOptions.max) {
      ff.validation.messages.max = (_err:any, field:IExtraFieldConfigurationObject):string => {
        return this.translationService.instant('GA.VALIDATION.MAX', { max: field.templateOptions.max });
      };
    }
    if (_.isNumber(ff.templateOptions.min)) {
      ff.validation.messages.min = (_err:any, field:IExtraFieldConfigurationObject):string => {
        return this.translationService.instant('GA.VALIDATION.MIN', { min: field.templateOptions.min });
      };
    }
    if (ff.type === FormlyType.SEARCH) {
      if (util.notNullOrUndefined(ff.templateOptions.autoclear) || !ff.templateOptions.autoclear) {
        ff.validation.messages.editable = this.translationService.instant('GA.FORM.SEARCH.NOT_VALID');
      }
    }
  }

  private addDefaultInputType(ff:IExtraFieldConfigurationObject) {
    if (ff.type === FormlyType.INPUT) {
      ff.templateOptions = ff.templateOptions || {};
      ff.templateOptions.type = ff.templateOptions.type || 'text';
    }
  }

  private addInputType(ff:IExtraFieldConfigurationObject) {
    if (ff.data && ff.data.inputType && ff.type === FormlyType.INPUT) {
      if (ff.data.inputType === 'positiveInteger') {
        makeNumberType(ff, /^\d*$/);
      } else if (ff.data.inputType === 'positiveReal') {
        makeNumberType(ff, /^\d*[\.,]?\d*/);
      }
    }

    function makeNumberType(ff:IExtraFieldConfigurationObject, pattern:RegExp | string) {
      ff.templateOptions.type = FormlyInputType.NUMBER;
      ff.templateOptions.pattern = pattern;
      ff.expressionProperties['templateOptions.pattern'] = () => {
        return pattern;
      };
    }
  }

  private readOnly(ff:IExtraFieldConfigurationObject) {
    return ff.type === FormlyType.VALUE;
  }

  // translation category determination at the level of field
  private getTranslationCategory():TranslationCategory {
    // use GA category when field's screen is 'ga' or field's label starts with 'GA.'
    // otherwise use the category determination based on the current URL
    if ((this.screen === TranslationCategory.GA || this.label && util.stringStartsWith(this.label, 'GA.'))) {
      return TranslationCategory.GA;
    } else {
      return this.stateService.currentTranslationCategory;
    }
  }

  private isEditableField():boolean {
    // fields belonging to 'GA' category are default not editable
    if (this.extraOptions.templateOptions.category === TranslationCategory.GA) {
      return false;
    } else if (util.notNullOrUndefinedOrEmpty(this.editable)) {
      return this.editable;
    }
    return true;
  }
}